import {defineStore} from 'pinia';
import {ref} from 'vue';
import type {ComponentContent} from '@/ts/types/dto/interventions-dto';

export const useBookmarksStore =
  /** Manages the user's bookmarked content components */
  defineStore('bookmarks-store', () => {
  const bookmarks = ref<ComponentContent[]>([]);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  // Bumped by the user's own save/un-save toggles only — never by setBookmarks. Watchers that
  // must react to a toggle (Explore's Saved results) cannot use the list itself: only the sidebar
  // tab hydrates it, so removing an item it never had leaves both the length and the ids
  // untouched. Counting toggles also keeps a re-hydration from being mistaken for one.
  const mutationCount = ref(0);

  const setBookmarks = (data: ComponentContent[]): void => {
    bookmarks.value = data;
  };

  const addBookmark = (bookmark: ComponentContent): void => {
    bookmarks.value = [...bookmarks.value, bookmark];
    mutationCount.value++;
  };

  const removeBookmark = (bookmark: ComponentContent): void => {
    bookmarks.value = bookmarks.value.filter(b => b.id !== bookmark.id);
    mutationCount.value++;
  };

  const setLoading = (state: boolean): void => {
    isLoading.value = state;
  };

  const setError = (msg: string | null): void => {
    error.value = msg;
  };

  const clearStore = (): void => {
    bookmarks.value = [];
    isLoading.value = false;
    error.value = null;
    // mutationCount is deliberately left alone: this is teardown (logout), not a toggle, and
    // resetting it to 0 would itself be a change that wakes the watchers hanging off it.
  };

  return {
    bookmarks,
    mutationCount,
    isLoading,
    error,
    setBookmarks,
    addBookmark,
    removeBookmark,
    setLoading,
    setError,
    clearStore,
  };
});
